import React, { useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from './AuthContext';
import { Profile } from './types';
import { fetchUserProfile } from './fetchUserProfile';
import { login, signup, logout, updatePassword, updateProfile } from './authOperations';
import { logger } from '@/lib/utils/logger';

const componentLogger = logger.forComponent('AuthProvider');

// Fetch user role from the user_roles table
const fetchUserRole = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_role', { _user_id: userId });

    if (error) {
      componentLogger.error('Error fetching user role', { userId }, error);
      return null;
    }

    return data || null;
  } catch (error) {
    componentLogger.error('Unexpected error fetching user role', { userId }, error as Error);
    return null;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleLoadError, setRoleLoadError] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authChangeProcessed, setAuthChangeProcessed] = useState(false);

  // Initialize auth state
  useEffect(() => {
    componentLogger.debug('Setting up auth state listener');
    
    // First set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        componentLogger.debug('Auth state changed', { event, hasSession: !!currentSession });
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setAuthChangeProcessed(true);

        if (currentSession?.user) {
          try {
            const [profileData, roleData] = await Promise.all([
              fetchUserProfile(currentSession.user.id),
              fetchUserRole(currentSession.user.id)
            ]);
            componentLogger.debug('Profile and role data fetched from auth state change', {
              hasProfile: !!profileData,
              role: roleData
            });
            setProfile(profileData);
            setUserRole(roleData);
            setRoleLoadError(roleData === null);
          } catch (error) {
            componentLogger.error('Error fetching profile/role in auth state change', {}, error as Error);
            setRoleLoadError(true);
          } finally {
            setLoading(false);
          }
        } else {
          setProfile(null);
          setUserRole(null);
          setRoleLoadError(false);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    const checkSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        componentLogger.debug('Initial session check', { hasSession: !!currentSession });
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          try {
            const [profileData, roleData] = await Promise.all([
              fetchUserProfile(currentSession.user.id),
              fetchUserRole(currentSession.user.id)
            ]);
            componentLogger.debug('Initial profile and role data fetched', {
              hasProfile: !!profileData,
              role: roleData
            });
            setProfile(profileData);
            setUserRole(roleData);
            setRoleLoadError(roleData === null);
          } catch (error) {
            componentLogger.error('Error fetching initial profile/role', {}, error as Error);
            setRoleLoadError(true);
          }
        }
        
        setLoading(false);
        setAuthChangeProcessed(true);
      } catch (error) {
        componentLogger.error('Error during initial session check', {}, error as Error);
        setLoading(false);
        setAuthChangeProcessed(true);
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    const result = await login(email, password);
    if (!result) {
      setLoading(false);
    }
    return result;
  };

  const handleUpdateProfile = async (data: Partial<Profile>): Promise<boolean> => {
    if (!user) return false;
    const result = await updateProfile(user.id, data);
    
    if (result) {
      // Update local state
      setProfile(prev => prev ? { ...prev, ...data } : null);
    }
    
    return result;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile,
      session,
      loading,
      authChangeProcessed,
      isAdmin: userRole === 'admin',
      roleLoadError,
      login: handleLogin, 
      signup,
      logout,
      updateProfile: handleUpdateProfile,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};
